import React, { createContext, useContext, useEffect, useState } from 'react';
import { useGoogleAnalytics } from '@/hooks/use-google-analytics';
import { supabase } from '@/integrations/supabase/client';
import { PurchaseEventData, UserProperties } from '@/lib/google-analytics';

interface GoogleAnalyticsContextType {
  trackPurchase: (data: PurchaseEventData) => void;
  trackSignup: (method: string) => void;
  trackLogin: (method: string) => void;
  trackSubscription: (action: 'subscribe' | 'cancel' | 'upgrade' | 'downgrade', planName: string, value?: number) => void;
  trackTrialStart: (planName: string) => void;
  trackCheckoutProgress: (step: number, stepName: string, value?: number) => void;
  trackFeatureUsage: (featureName: string, action: string) => void;
  trackPageView: (path?: string, title?: string) => void;
  setUserProperties: (properties: UserProperties) => void;
}

const GoogleAnalyticsContext = createContext<GoogleAnalyticsContextType | null>(null);

interface GoogleAnalyticsProviderProps {
  children: React.ReactNode;
  measurementId?: string;
}

export function GoogleAnalyticsProvider({ 
  children, 
  measurementId = 'G-SZYK3BQF2F'
}: GoogleAnalyticsProviderProps) {
  // Check if we're in a browser environment
  if (typeof window === 'undefined') {
    return <>{children}</>;
  }

  // Check if React is available
  if (!React || !React.useState) {
    console.error('React is not available in GoogleAnalyticsProvider');
    return <>{children}</>;
  }

  // Try adding useState first
  const [user, setUser] = useState<any>(null);

  // For now, return a simple provider without the useGoogleAnalytics hook
  const mockAnalytics = {
    trackPurchase: () => {},
    trackSignup: () => {},
    trackLogin: () => {},
    trackSubscription: () => {},
    trackTrialStart: () => {},
    trackCheckoutProgress: () => {},
    trackFeatureUsage: () => {},
    trackPageView: () => {},
    setUserProperties: () => {},
  };

  return (
    <GoogleAnalyticsContext.Provider value={mockAnalytics}>
      {children}
    </GoogleAnalyticsContext.Provider>
  );
}

export function useAnalytics() {
  const context = useContext(GoogleAnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within a GoogleAnalyticsProvider');
  }
  return context;
}